import type { CSSProperties, ReactNode } from 'react';
import { buildReferralUrl } from '../lib/referral.js';

export type BrandColors = {
  /** Main brand color — applied to header background */
  primary: string;
  /** Accent / CTA color — applied to powered-by badge */
  accent?: string;
  /** Viewer background override */
  bg?: string;
};

export type BrandingShellProps = {
  /** Agency logo image URL */
  logoUrl: string;
  /** Agency brand colors */
  brandColors: BrandColors;
  /** Show "powered by CloudBTL" referral mark (true = free tier, false = paid) */
  poweredByVisible: boolean;
  /** ref= param — sending agency identity (branding ID) */
  refParam?: string;
  /** src= param — source proposal identity (e.g. "proposal_<id>") */
  srcParam?: string;
  /** Proposal / document viewer content */
  children?: ReactNode;
};

/**
 * BrandingShell — wraps the document viewer in agency logo + brand colors.
 * Free tier: shows "powered by CloudBTL" badge with referral URL.
 * Paid tier: badge omitted (poweredByVisible=false).
 */
export function BrandingShell({
  logoUrl,
  brandColors,
  poweredByVisible,
  refParam,
  srcParam,
  children,
}: BrandingShellProps): JSX.Element {
  const cssVars: CSSProperties = {
    '--brand-primary': brandColors.primary,
    '--brand-accent': brandColors.accent ?? brandColors.primary,
    '--brand-bg': brandColors.bg ?? '#ffffff',
  } as CSSProperties;

  const poweredByHref = (() => {
    if (refParam || srcParam) {
      // Extract proposalId from "proposal_<id>" prefix if present
      const agencyId = refParam ?? '';
      const proposalId = srcParam?.startsWith('proposal_')
        ? srcParam.slice('proposal_'.length)
        : (srcParam ?? '');
      return buildReferralUrl(proposalId, agencyId);
    }
    return 'https://cloudbtl.com';
  })();

  return (
    <div className="branding-shell" style={cssVars} data-testid="branding-shell">
      <header className="branding-shell__header" data-testid="branding-shell-header">
        <img
          src={logoUrl}
          alt="Agency logo"
          className="branding-shell__logo"
          data-testid="branding-shell-logo"
        />
      </header>

      <div className="branding-shell__content" data-testid="branding-shell-content">
        {children}
      </div>

      {poweredByVisible && (
        <a
          href={poweredByHref}
          className="branding-shell__powered-by"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="branding-shell-powered-by"
          aria-label="Powered by CloudBTL"
        >
          powered by <strong>CloudBTL</strong>
        </a>
      )}
    </div>
  );
}
